import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { ListViewOrderBy } from "@/services/list-view";
import { ArrowUpDown, X } from "lucide-react";
import { useMemo, useState } from "react";
import { SegmentedControl } from "./ds";
import { FieldPicker } from "./filter-tree/field-picker";
import { GeoOriginPicker } from "./filter-tree/geo-input";
import { DISTANCE_OPERATORS, geoValueOf, humanizePath, originLabel } from "./filter-tree/tree-utils";
import type { FilterFieldType, FilterNode, FilterTree, GeoOrigin, SchemaField } from "./filter-tree/types";
import { useResolvedPath } from "./filter-tree/use-filter-schema";

/** A column of the list, sortable by a click on its header too. */
export type SortColumn = { fieldName: string; label: string };

const sortable = (field: SchemaField) => field.sortable !== false;

/**
 * The origin of the one distance condition of a tree on `column`, in its groups but outside
 * related records; none when several measure from different places (as the server reads it,
 * `crm.filter_tree.geo.tree_origin`).
 */
function treeOrigin(tree: FilterTree | null | undefined, column: string): GeoOrigin | null {
    const origins = new Map<string, GeoOrigin>();
    const visit = (node: FilterNode) => {
        if (node.type === "group") node.children.forEach(visit);
        if (node.type !== "condition" || node.field !== column || !DISTANCE_OPERATORS.has(node.operator)) return;
        const origin = geoValueOf(node.value).origin;
        if (origin) origins.set(JSON.stringify(origin), origin);
    };
    if (tree) visit(tree.root);
    return origins.size === 1 ? [...origins.values()][0] : null;
}

function directionLabels(type?: FilterFieldType): [string, string] {
    switch (type) {
        case "geo":
            return ["Nearest first", "Farthest first"];
        case "date":
        case "datetime":
            return ["Oldest first", "Newest first"];
        case "number":
            return ["Smallest first", "Largest first"];
        default:
            return ["A → Z", "Z → A"];
    }
}

/**
 * « Sort »: what a click on a column header does, and more: a field of a linked object
 * (Account › Name), or the distance of a location from a place or a record
 * (`server/crm/filter_tree/geo.py`), which starts from the list's distance filter when it has one.
 */
export function SortControl({
    rootModel,
    orderBy,
    columns,
    tree,
    onChange
}: {
    rootModel: string;
    orderBy: ListViewOrderBy | null;
    /** The columns of the list: offered first, and named as their header names them. */
    columns: SortColumn[];
    /** The advanced filters of the list: their distance condition gives a location sort its origin. */
    tree?: FilterTree | null;
    onChange: (orderBy: ListViewOrderBy | null) => void;
}) {
    const [open, setOpen] = useState(false);
    const [column, setColumn] = useState("");
    const [origin, setOrigin] = useState<GeoOrigin | null>(null);
    const [direction, setDirection] = useState<"asc" | "desc">("asc");
    const resolved = useResolvedPath(rootModel, column);
    const activeResolved = useResolvedPath(rootModel, orderBy?.column ?? "");
    const extraRootFields = useMemo(() => columns.map(item => ({ name: item.fieldName, label: item.label })), [columns]);

    const columnLabel = (path: string) => columns.find(item => item.fieldName === path)?.label;
    const labelsOf = (path: string, resolvedPath: typeof resolved) => resolvedPath?.labels ?? (path ? [columnLabel(path) ?? humanizePath(path)] : []);
    const isGeo = resolved?.field?.type === "geo";
    const [ascending, descending] = directionLabels(resolved?.field?.type);
    // The header arrow already shows a sort on a column of the list: the button names the others.
    const hidden = orderBy && (orderBy.origin || !columns.some(item => item.fieldName === orderBy.column));
    const activeLabel = orderBy ? (orderBy.origin ? originLabel(orderBy.origin) : labelsOf(orderBy.column, activeResolved).join(" › ")) : "";

    const openWith = (next: boolean) => {
        if (next) {
            setColumn(orderBy?.column ?? "");
            setOrigin(orderBy?.origin ?? null);
            setDirection(orderBy?.direction ?? "asc");
        }
        setOpen(next);
    };
    const pickColumn = (path: string) => {
        setColumn(path);
        setOrigin(previous => treeOrigin(tree, path) ?? previous);
    };
    const apply = () => {
        if (!column) return;
        onChange({ column, direction, origin: isGeo ? origin : null });
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={openWith} modal>
            <PopoverTrigger asChild>
                <Button variant="outline" className={cn("group relative m-0 p-2", hidden && "border-primary text-primary")} title={orderBy ? `Sorted by ${activeLabel}` : undefined}>
                    <ArrowUpDown size={16} className="flex-shrink-0" />
                    {!hidden && (
                        <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center whitespace-nowrap rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                            <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                            Sort
                        </div>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="flex w-[380px] flex-col gap-3 p-3" align="end" collisionPadding={12}>
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Sort</h3>
                    {orderBy && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-gray-600"
                            onClick={() => {
                                onChange(null);
                                setOpen(false);
                            }}
                        >
                            <X className="mr-1 h-3.5 w-3.5" /> Clear
                        </Button>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-600">Sort by</span>
                    <FieldPicker
                        rootModel={rootModel}
                        labels={labelsOf(column, resolved)}
                        allowRelated={false}
                        filterField={sortable}
                        extraRootFields={extraRootFields}
                        className="w-full"
                        onPick={pick => pickColumn(pick.path)}
                    />
                    <span className="text-xs text-gray-500">A field of the list or of a linked object, or a location to sort by distance.</span>
                </div>
                {isGeo && (
                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-gray-600">Distance from</span>
                        <GeoOriginPicker value={origin} onChange={setOrigin} rootModel={rootModel} defaultPoint={column} className="w-full max-w-none" />
                    </div>
                )}
                <SegmentedControl
                    ariaLabel="Order"
                    value={direction}
                    onChange={setDirection}
                    options={[
                        { value: "asc", label: ascending },
                        { value: "desc", label: descending }
                    ]}
                />
                {isGeo && <p className="text-xs text-gray-500">Records without a location come last.</p>}
                <div className="flex justify-end">
                    <Button type="button" size="sm" disabled={!column || resolved === undefined || (isGeo && !origin)} onClick={apply}>
                        Sort
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
